import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "../components/AdminLayout";
import { AdminApi } from "../api/admin-api";
import { toast } from "sonner";

export default function ManageTeamMembers() {
  const [teamIdInput, setTeamIdInput] = useState("");
  const [searchedTeamId, setSearchedTeamId] = useState<number | null>(null);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const queryClient = useQueryClient();

  const {
    data: team,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["adminTeamMembers", searchedTeamId],
    queryFn: () => AdminApi.getTeamWithMembers(searchedTeamId!),
    enabled: searchedTeamId !== null,
    retry: false,
  });

  const addMemberMutation = useMutation({
    mutationFn: (email: string) =>
      AdminApi.addTeamMember(searchedTeamId!, email),
    onSuccess: () => {
      toast.success("Member added successfully");
      setNewMemberEmail("");
      queryClient.invalidateQueries({
        queryKey: ["adminTeamMembers", searchedTeamId],
      });
    },
    onError: (err) => {
      toast.error(`Failed to add member: ${err.message}`);
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseInt(teamIdInput.trim());
    if (!isNaN(parsed)) {
      setSearchedTeamId(parsed);
    }
  };

  const handleAddMember = (e: React.FormEvent) => {
    e.preventDefault();
    if (newMemberEmail.trim()) {
      addMemberMutation.mutate(newMemberEmail.trim());
    }
  };

  return (
    <AdminLayout
      title="Manage Team Members"
      description="Look up a team by ID, view its members, and add new members"
      breadcrumb="Manage Team Members"
    >
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleSearch} className="flex gap-3">
            <input
              type="number"
              value={teamIdInput}
              onChange={(e) => setTeamIdInput(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Team ID"
              required
            />
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? "Loading..." : "Look up"}
            </button>
          </form>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-sm">
              {error instanceof Error ? error.message : "Team not found"}
            </p>
          </div>
        )}

        {team && (
          <>
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">
                  {team.name || "(unnamed)"}
                </h3>
                <p className="text-sm text-gray-500">
                  ID: {team.id} &middot; {team.externalId}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {team.members.length} member
                  {team.members.length !== 1 ? "s" : ""}
                </p>
              </div>

              {team.members.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  This team has no members.
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {team.members.map((member) => (
                    <div
                      key={member.id}
                      className="px-6 py-4 flex items-center justify-between"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {member.email}
                        </p>
                        <p className="text-sm text-gray-500">
                          {[member.firstName, member.lastName]
                            .filter(Boolean)
                            .join(" ") || "(no name)"}{" "}
                          &middot; {member.role} &middot; User ID:{" "}
                          {member.userId}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 mb-3">
                Add member by email
              </h3>
              <form onSubmit={handleAddMember} className="flex gap-3">
                <input
                  type="email"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="user@example.com"
                  required
                />
                <button
                  type="submit"
                  disabled={addMemberMutation.isPending}
                  className="px-6 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {addMemberMutation.isPending ? "Adding..." : "Add"}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
